import { Injectable } from '@angular/core';

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Single entry point for the tenant brand colour.
 *
 * Writes the four brand tokens defined in `src/styles.scss`
 * (`--primary`, `--primary-rgb`, `--primary-dark`, `--primary-foreground`);
 * every brand-coloured surface in the app derives from those.
 *
 * The tokens are set as *inline* styles on `documentElement` on purpose:
 * ngx-print copies only `<style>`/`<link>` tags into its print popup, so
 * inline custom properties never reach the printed bill.
 */
@Injectable({
  providedIn: 'root'
})
export class BrandingService {

  /** Re-apply the stored brand on bootstrap/refresh. */
  applyStoredBrand(): void {
    const hex = sessionStorage.getItem('brandHex');
    if (hex) {
      this.applyBrand(hex);
    }
  }

  /** Apply a tenant brand hex. Invalid input is ignored so the default theme survives. */
  applyBrand(hex: string): void {
    const rgb = hexToRgb(hex);
    if (!rgb) {
      return;
    }
    const { h, s, l } = rgbToHsl(rgb);
    const style = document.documentElement.style;
    style.setProperty('--primary', `hsl(${h}, ${s}%, ${l}%)`);
    style.setProperty('--primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    style.setProperty('--primary-dark', `hsl(${h}, ${s}%, ${Math.max(l - 8, 12)}%)`);
    style.setProperty('--primary-foreground', relativeLuminance(rgb) > 0.45 ? '#000000' : '#FFFFFF');
  }

  /** Resolve the brand colour to a literal, for chart libraries and nz-tag bindings. */
  getBrandColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
  }

  /** Brand colour as an rgba() string at the given alpha, for chart fills. */
  getBrandColorAlpha(alpha: number): string {
    const channels = getComputedStyle(document.documentElement).getPropertyValue('--primary-rgb').trim();
    return `rgba(${channels}, ${alpha})`;
  }
}

/** Accepts `#RGB` / `#RRGGBB` (with or without the leading `#`). Returns null otherwise. */
function hexToRgb(hex: string): Rgb | null {
  const value = hex.trim().replace(/^#/, '');
  const full = value.length === 3
    ? value.split('').map(c => c + c).join('')
    : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return null;
  }
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
    }
    h *= 60;
    if (h < 0) {
      h += 360;
    }
  }
  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/** WCAG relative luminance — decides whether text on the brand colour is black or white. */
function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
